import React, { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Target,
  Award,
  Heart,
  Star,
  Globe,
} from "lucide-react";
import aboutHero from "../assets/images/aboutHero.jpg";

function About() {
  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div className="bg-white min-h-screen">
      <Header />

      {/* HERO SECTION */}
      <section className="w-4/5 mx-auto py-28 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div data-aos="fade-right" className="space-y-6">
          <h1 className="text-2xl sm:text-4xl md:text-5xl font-extrabold text-gray-900 leading-tight">
            About <span className="text-[#007AFF]">Dotti Verify</span>
          </h1>
          <p className="text-m sm:text-base md:text-lg text-gray-700 leading-relaxed max-w-xl">
            We’re on a mission to make identity verification and payments
            simpler, faster, and safer for businesses and individuals across
            Africa and beyond. Since our launch, we’ve helped thousands of
            people build trust in the digital world.
          </p>
        </div>
        <div data-aos="fade-left" className="flex justify-center">
          <img
            src={aboutHero}
            alt="About Us"
            className="rounded-2xl shadow-xl w-full h-[450px] object-cover"
          />
        </div>
      </section>

      {/* OUR STORY */}
      <section className="bg-gray-50 py-8 sm:py-16 md:py-24">
        <div className="w-4/5 mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl md:text-4xl font-bold text-gray-900 mb-6">
            Our Story
          </h2>
          <p className="text-m sm:text-base md:text-lg text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Dotti Verify started with a simple idea — that people should be able
            to trust who they interact with online. With fraud on the rise, we
            knew businesses needed a tool that was not just powerful, but also
            easy to use. Today, we empower thousands of individuals and
            organizations to verify users, pay bills, and transact securely,
            without stress or delays.
          </p>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="w-4/5 py-8 sm:py-16 md:py-24 mx-auto grid grid-cols-1 md:grid-cols-2 gap-12">
        <div data-aos="fade-up" className="bg-white p-10 rounded-2xl shadow-lg">
          <Target className="w-12 h-12 text-[#007AFF] mb-4" />
          <h3 className="text-xl sm:text-4xl md:text-2xl font-bold mb-4">
            Our Mission
          </h3>
          <p className="text-m sm:text-base md:text-lg text-gray-700 leading-relaxed">
            To provide secure, reliable, and fast identity verification and
            digital payment services that empower trust in every transaction.
          </p>
        </div>
        <div
          data-aos="fade-up"
          data-aos-delay="200"
          className="bg-white p-10 rounded-2xl shadow-lg"
        >
          <Globe className="w-12 h-12 text-[#007AFF] mb-4" />
          <h3 className="text-xl sm:text-4xl md:text-2xl font-bold mb-4">
            Our Vision
          </h3>
          <p className="text-m sm:text-base md:text-lg text-gray-700 leading-relaxed">
            To be Africa’s most trusted digital verification and payment
            platform, connecting people and businesses across the globe.
          </p>
        </div>
      </section>

      {/* CORE VALUES */}
      <section className="bg-gray-50 py-24">
        <div className="w-4/5 mx-auto text-center">
          <h2 className="text-2xl sm:text-4xl md:text-4xl font-bold text-gray-900 mb-12">
            Our Core Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <Award className="w-10 h-10 text-[#007AFF] mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Excellence</h3>
              <p className="text-m sm:text-base md:text-base text-gray-600">
                We strive to deliver top-quality services every time.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <Heart className="w-10 h-10 text-[#007AFF] mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Integrity</h3>
              <p className="text-m sm:text-base md:text-base text-gray-600">
                Honesty and transparency guide every action we take.
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow hover:shadow-lg transition">
              <Star className="w-10 h-10 text-[#007AFF] mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">Innovation</h3>
              <p className="text-m sm:text-base md:text-base text-gray-600">
                We keep pushing boundaries to create better solutions.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* OUR JOURNEY (TIMELINE) */}
      <section className="py-24 w-4/5 mx-auto">
        <h2 className="text-2xl sm:text-4xl md:text-4xl font-bold text-gray-900 text-center mb-12">
          Our Journey
        </h2>
        <div className="space-y-12">
          <div className="flex items-start gap-6" data-aos="fade-up">
            <div
              className="
    w-16 h-16
    sm:w-20 sm:h-20
    bg-[#007AFF] text-white 
    rounded-full flex items-center justify-center 
    font-bold text-xs sm:text-sm md:text-base
    px-2 sm:px-0   /* extra padding on mobile only */
  "
            >
              2019
            </div>

            <p className="text-m sm:text-base md:text-lg text-gray-700">
              The idea for Dotti Verify was born, driven by the growing demand
              for digital trust and security.
            </p>
          </div>
          <div
            className="flex items-start gap-6"
            data-aos="fade-up"
            data-aos-delay="200"
          >
            <div
              className="
    w-16 h-16
    sm:w-20 sm:h-20
    bg-[#007AFF] text-white 
    rounded-full flex items-center justify-center 
    font-bold text-xs sm:text-sm md:text-base
    px-2 sm:px-0   /* extra padding on mobile only */
  "
            >
              2021
            </div>

            <p className="text-m sm:text-base md:text-lg text-gray-700">
              We launched our first platform, serving small businesses across
              Nigeria.
            </p>
          </div>
          <div
            className="flex items-start gap-6"
            data-aos="fade-up"
            data-aos-delay="400"
          >
            <div
              className="
    w-16 h-16
    sm:w-20 sm:h-20
    bg-[#007AFF] text-white 
    rounded-full flex items-center justify-center 
    font-bold text-xs sm:text-sm md:text-base
    px-2 sm:px-0   /* extra padding on mobile only */
  "
            >
              2023
            </div>

            <p className="text-m sm:text-base md:text-lg text-gray-700">
              Expanded into more services including bill payments, airtime, and
              cable subscriptions.
            </p>
          </div>
          <div
            className="flex items-start gap-6"
            data-aos="fade-up"
            data-aos-delay="600"
          >
            <div
              className="
    w-16 h-16
    sm:w-20 sm:h-20
    bg-[#007AFF] text-white 
    rounded-full flex items-center justify-center 
    font-bold text-xs sm:text-sm md:text-base
    px-2 sm:px-0   /* extra padding on mobile only */
  "
            >
              2025
            </div>

            <p className="text-m sm:text-base md:text-lg text-gray-700">
              Now empowering thousands of users daily with trusted verifications
              and seamless transactions.
            </p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-gray-900 py-24 text-center text-white">
        <div className="w-4/5 mx-auto">
          <h2 className="text-2xl sm:text-5xl md:text-5xl font-bold mb-6">
            Join <span className="text-[#007AFF]">Dotti Verify</span> Today
          </h2>
          <p className="text-m sm:text-2xl md:text-2xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Be part of a growing community that values security, trust, and
            innovation. Let’s build the future together.
          </p>
          <a
            href="/signup"
            className="bg-[#007AFF] text-white font-semibold text-lg px-8 py-4 rounded-full shadow-lg hover:bg-blue-600 transition"
          >
            Get Started
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default About;
